/**
 * V2.0 Phase 4a — Mortgage Intake page (/mortgage-intake).
 *
 * A single-form view where the user provides the mortgage-specific inputs
 * (income, debt, savings, employment, target home) and immediately sees
 * their Mortgage Readiness Score, Purchase Power, program eligibility and
 * top three improvement levers. All fields are optional so partial saves
 * work — the engine returns `has_intake: false` + `missing_inputs: [...]`
 * when critical inputs are absent, and we surface those to the user.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Home, Loader2, TrendingUp, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AppNav from "@/components/app/AppNav";

const EMPLOYMENT_OPTIONS = [
  { value: "w2", label: "W-2 employed" },
  { value: "self_employed", label: "Self-employed" },
  { value: "1099_contractor", label: "1099 contractor" },
  { value: "retired", label: "Retired" },
  { value: "unemployed", label: "Currently unemployed" },
  { value: "other", label: "Other" },
];

const HOUSING_OPTIONS = [
  { value: "renting", label: "Renting" },
  { value: "own_with_mortgage", label: "Own with a mortgage" },
  { value: "own_outright", label: "Own outright" },
  { value: "first_time_buyer", label: "First-time buyer" },
];

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const MISSING_LABEL = {
  household_annual_income: "Household annual income",
  monthly_debt_payments_total: "Monthly debt payments",
  down_payment_savings_available: "Down payment savings",
  employment_status: "Employment status",
  target_home_price_max: "Target home price (max)",
};

const TIER_TONE = {
  mortgage_ready: { fg: "var(--ds-color-success)", bg: "var(--ds-color-success-soft)", border: "var(--ds-color-success-soft)" },
  nearly_ready: { fg: "var(--ds-color-warning)", bg: "var(--ds-color-warning-soft)", border: "var(--ds-color-warning-soft)" },
  needs_improvement: { fg: "var(--ds-color-warning)", bg: "var(--ds-color-warning-soft)", border: "var(--ds-color-warning-soft)" },
  build_foundation: { fg: "var(--ds-color-danger)", bg: "var(--ds-color-danger-soft)", border: "var(--ds-color-danger-soft)" },
};

function toNumberOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtDollar(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString()}`;
}

export default function MortgageIntake() {
  const { user } = useAuth();
  const initial = useMemo(() => {
    const src = user?.mortgage_intake || {};
    return {
      household_annual_income: src.household_annual_income ?? "",
      monthly_debt_payments_total: src.monthly_debt_payments_total ?? "",
      down_payment_savings_available: src.down_payment_savings_available ?? "",
      employment_status: src.employment_status ?? "",
      years_current_employer: src.years_current_employer ?? "",
      years_self_employed: src.years_self_employed ?? "",
      homeownership_status: src.homeownership_status ?? "",
      first_time_buyer: src.first_time_buyer ?? false,
      target_home_price_min: src.target_home_price_min ?? "",
      target_home_price_max: src.target_home_price_max ?? "",
      target_property_state: src.target_property_state ?? "",
      target_property_zip: src.target_property_zip ?? "",
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [readiness, setReadiness] = useState(null);
  // V2 Phase 5 Cluster B closeout — success callout state. Set true after a
  // successful save so we can render an inline next-step card (Dashboard /
  // Opportunities) instead of leaving the user dead-ended on the same page.
  // Reset the moment the user edits any field so the callout doesn't
  // linger over stale, unsaved input.
  const [justSaved, setJustSaved] = useState(false);

  function upd(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    if (justSaved) setJustSaved(false);
  }

  const loadReadiness = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mortgage/readiness");
      setReadiness(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReadiness(); }, [loadReadiness]);

  async function onSave(e) {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        household_annual_income: toNumberOrNull(form.household_annual_income),
        monthly_debt_payments_total: toNumberOrNull(form.monthly_debt_payments_total),
        down_payment_savings_available: toNumberOrNull(form.down_payment_savings_available),
        employment_status: form.employment_status || null,
        years_current_employer: toNumberOrNull(form.years_current_employer),
        years_self_employed: toNumberOrNull(form.years_self_employed),
        homeownership_status: form.homeownership_status || null,
        first_time_buyer: !!form.first_time_buyer,
        target_home_price_min: toNumberOrNull(form.target_home_price_min),
        target_home_price_max: toNumberOrNull(form.target_home_price_max),
        target_property_state: form.target_property_state || null,
        target_property_zip: form.target_property_zip || null,
      };
      await api.post("/user/mortgage-intake", payload);
      await loadReadiness();
      setJustSaved(true);
      toast.success("Mortgage intake saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="mortgage-intake-page" data-page-outer="cluster-b"
      className="min-h-screen text-[var(--ds-color-ink)] bg-[var(--ds-color-background)]" style={{ background: "var(--ds-color-background)", color: "var(--ds-color-ink)" }}>
      <AppNav />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <Link
          to="/dashboard"
          data-testid="mortgage-intake-back"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Back to dashboard
        </Link>

        <header className="mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-accent)] mb-2">
            <Home className="w-3.5 h-3.5" /> Mortgage Readiness
          </div>
          <h1 className="font-semibold tracking-tight text-[30px] md:text-[36px] tracking-tight">
            Where are you on the path to homeownership?
          </h1>
          <p
            data-testid="mortgage-page-subtitle"
            className="text-sm text-[var(--ds-color-ink-muted)] mt-2 max-w-2xl"
          >
            Evaluate your home-buying readiness and estimate your purchase power.
          </p>
        </header>

        {justSaved && (
          <section
            data-testid="mortgage-intake-success"
            role="status"
            aria-live="polite"
            className="mb-8 rounded-[var(--ds-radius-card)] p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            style={{ background: "var(--ds-color-success-soft)", border: "1px solid var(--ds-color-success)" }}
          >
            <div className="flex items-start gap-3">
              <CheckCircle2
                className="w-5 h-5 mt-0.5 shrink-0"
                style={{ color: "var(--ds-color-success)" }}
                aria-hidden="true"
              />
              <div>
                <div
                  className="text-sm md:text-base font-semibold"
                  style={{ color: "var(--ds-color-success)" }}
                  data-testid="mortgage-intake-success-title"
                >
                  Mortgage profile saved.
                </div>
                <p className="mt-1 text-sm text-[var(--ds-color-ink-muted)] max-w-lg">
                  {readiness?.has_intake && readiness?.mortgage_readiness_score != null
                    ? "We've refreshed your readiness score. Continue on to see what to focus on next."
                    : "We've captured what you shared. Add the remaining fields to unlock your readiness score, or continue on to explore your next steps."}
                </p>
                {readiness?.has_intake &&
                 readiness?.mortgage_readiness_score != null &&
                 readiness?.mortgage_readiness_tier?.label && (
                  <div
                    data-testid="mortgage-intake-success-tier-chip"
                    className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium"
                    style={(() => {
                      const tone =
                        TIER_TONE[readiness.mortgage_readiness_tier.code] ||
                        TIER_TONE.build_foundation;
                      return {
                        background: tone.bg,
                        color: tone.fg,
                        border: `1px solid ${tone.border}`,
                      };
                    })()}
                  >
                    You&rsquo;re {readiness.mortgage_readiness_tier.label} ·{" "}
                    {readiness.mortgage_readiness_score}/100
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Link
                to="/dashboard"
                data-testid="mortgage-intake-success-cta-dashboard"
                className="inline-flex items-center justify-center gap-1.5 rounded-[var(--ds-radius-md)] px-4 py-2 text-sm font-medium"
                style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-accent-ink)" }}
              >
                Go to Dashboard <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
              <Link
                to="/opportunities"
                data-testid="mortgage-intake-success-cta-opportunities"
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[var(--ds-color-border)] px-4 py-2 text-sm font-medium text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)]"
              >
                View Opportunities
              </Link>
            </div>
          </section>
        )}

        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] gap-8">
          <form onSubmit={onSave} className="space-y-6" data-testid="mortgage-intake-form">
            <FieldGroup title="Household finances">
              <NumField testId="mortgage-input-income" label="Household annual income (before tax)" prefix="$" value={form.household_annual_income} onChange={(v) => upd("household_annual_income", v)} placeholder="e.g. 90000" />
              <NumField testId="mortgage-input-debt" label="Monthly debt payments (excl. rent/utilities)" prefix="$" value={form.monthly_debt_payments_total} onChange={(v) => upd("monthly_debt_payments_total", v)} placeholder="e.g. 600" />
              <NumField testId="mortgage-input-down" label="Down payment savings available" prefix="$" value={form.down_payment_savings_available} onChange={(v) => upd("down_payment_savings_available", v)} placeholder="e.g. 25000" />
            </FieldGroup>

            <FieldGroup title="Employment">
              <SelectField testId="mortgage-input-employment" label="Employment status" options={EMPLOYMENT_OPTIONS} value={form.employment_status} onChange={(v) => upd("employment_status", v)} />
              {form.employment_status === "w2" && (
                <NumField testId="mortgage-input-years-employer" label="Years at current employer" value={form.years_current_employer} onChange={(v) => upd("years_current_employer", v)} placeholder="e.g. 3" step="0.5" />
              )}
              {["self_employed", "1099_contractor"].includes(form.employment_status) && (
                <NumField testId="mortgage-input-years-self" label="Years self-employed" value={form.years_self_employed} onChange={(v) => upd("years_self_employed", v)} placeholder="e.g. 4" step="0.5" />
              )}
            </FieldGroup>

            <FieldGroup title="Housing history">
              <SelectField testId="mortgage-input-housing" label="Current housing status" options={HOUSING_OPTIONS} value={form.homeownership_status} onChange={(v) => upd("homeownership_status", v)} />
              <label className="flex items-center gap-2 text-sm text-[var(--ds-color-ink-muted)]">
                <input
                  type="checkbox"
                  data-testid="mortgage-input-first-time"
                  checked={!!form.first_time_buyer}
                  onChange={(e) => upd("first_time_buyer", e.target.checked)}
                  className="min-w-0 accent-[var(--ds-color-accent)]"
                />
                I&rsquo;m a first-time home buyer
              </label>
            </FieldGroup>

            <FieldGroup title="Target home">
              <div className="grid grid-cols-2 gap-3">
                <NumField testId="mortgage-input-price-min" label="Target price — min" prefix="$" value={form.target_home_price_min} onChange={(v) => upd("target_home_price_min", v)} placeholder="e.g. 250000" />
                <NumField testId="mortgage-input-price-max" label="Target price — max" prefix="$" value={form.target_home_price_max} onChange={(v) => upd("target_home_price_max", v)} placeholder="e.g. 400000" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <SelectField testId="mortgage-input-state" label="Target state" options={STATES.map((s) => ({ value: s, label: s }))} value={form.target_property_state} onChange={(v) => upd("target_property_state", v)} />
                <TextField testId="mortgage-input-zip" label="Target ZIP" value={form.target_property_zip} onChange={(v) => upd("target_property_zip", v)} placeholder="5-digit ZIP" maxLength={5} />
              </div>
            </FieldGroup>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                data-testid="mortgage-intake-save"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-sm font-medium disabled:opacity-50"
                style={{ background: "var(--ds-color-accent)", color: "var(--ds-color-background)" }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Saving…" : "Save & recalculate"}
              </button>
              <span className="text-xs text-[var(--ds-color-ink-muted)]">Fields are optional — save whatever you know so far.</span>
            </div>
          </form>

          <aside className="space-y-4">
            {loading && !readiness ? (
              <div className="rounded-[var(--ds-radius-card)] border border-[var(--ds-color-border)] p-6 text-sm text-[var(--ds-color-ink-muted)]" style={{ background: "var(--ds-color-surface)" }}>
                Loading your readiness…
              </div>
            ) : (
              <ReadinessPanel data={readiness} />
            )}
            {readiness?.compliance_note && (
              <div
                data-testid="mortgage-compliance-note"
                className="rounded-[var(--ds-radius-field)] px-4 py-3 text-[11px] leading-relaxed"
                style={{ background: "var(--ds-color-surface-muted)", border: "1px solid var(--ds-color-border)", color: "var(--ds-color-ink-muted)" }}
              >
                {readiness.compliance_note}
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}

function ReadinessPanel({ data }) {
  if (!data) return null;
  if (!data.has_intake) {
    return (
      <div
        data-testid="mortgage-readiness-empty"
        className="rounded-[var(--ds-radius-card)] p-6 space-y-3"
        style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)" }}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-ink-muted)]">Mortgage readiness</div>
        <div className="font-semibold tracking-tight text-[24px]">Add a few fields to see your score.</div>
        <p className="text-xs text-[var(--ds-color-ink-muted)]">We need at least income, debt, savings, employment, and a target home price to calculate.</p>
        {data.missing_inputs?.length > 0 && (
          <ul className="text-xs space-y-1 mt-2" data-testid="mortgage-missing-inputs">
            {data.missing_inputs.map((k) => (
              <li key={k} className="text-[var(--ds-color-ink-muted)]">• {MISSING_LABEL[k] || k}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  const tier = data.mortgage_readiness_tier || {};
  const tone = TIER_TONE[tier.code] || TIER_TONE.build_foundation;
  const pp = data.purchase_power || null;
  return (
    <div className="space-y-4" data-testid="mortgage-readiness-panel">
      <div
        className="rounded-[var(--ds-radius-card)] p-6 space-y-3"
        style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)" }}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-ink-muted)]">Mortgage readiness</div>
        <div className="flex items-baseline gap-3">
          <div
            data-testid="mortgage-readiness-score"
            className="font-semibold tracking-tight text-[48px] font-semibold"
            style={{ color: tone.fg }}
          >
            {data.mortgage_readiness_score}
          </div>
          <div className="text-xs text-[var(--ds-color-ink-muted)]">/ 100</div>
        </div>
        <div
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-[0.18em]"
          style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }}
          data-testid="mortgage-readiness-tier"
        >
          {tier.emoji || ""} {tier.label || "—"}
        </div>
      </div>

      {pp && pp.estimated_purchase_power_max > 0 && (
        <div
          data-testid="mortgage-purchase-power"
          className="rounded-[var(--ds-radius-card)] p-6 space-y-4"
          style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)" }}
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-ink-muted)]">
            <TrendingUp className="w-3 h-3" /> Estimated purchase power
          </div>
          <div className="font-semibold tracking-tight text-[30px]">{fmtDollar(pp.estimated_purchase_power_max)}</div>
          <div className="text-xs text-[var(--ds-color-ink-muted)]">
            Assumes {fmtDollar(pp.assumed_down_payment)} down, monthly payment {fmtDollar(pp.estimated_monthly_payment)}.
          </div>
          <div className="border-t border-[var(--ds-color-border)] pt-3 space-y-1.5 text-xs">
            <PPRow label="Principal & interest" value={pp.payment_breakdown?.principal_interest} />
            <PPRow label="Property tax" value={pp.payment_breakdown?.property_tax} />
            <PPRow label="Homeowners insurance" value={pp.payment_breakdown?.homeowners_insurance} />
            <PPRow label="PMI" value={pp.payment_breakdown?.pmi} />
            <PPRow label="HOA" value={pp.payment_breakdown?.hoa} />
          </div>
        </div>
      )}

      {data.recommended_programs?.length > 0 && (
        <div
          data-testid="mortgage-programs"
          className="rounded-[var(--ds-radius-card)] p-5 space-y-3"
          style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)" }}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-ink-muted)]">Loan programs</div>
          <ul className="space-y-2.5">
            {data.recommended_programs.map((p) => (
              <li key={p.code} data-testid={`mortgage-program-${p.code}`} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-[var(--ds-color-ink)]">{p.label}</span>
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em]"
                    style={{
                      background: p.eligibility === "eligible" ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)",
                      color: p.eligibility === "eligible" ? "var(--ds-color-success)" : "var(--ds-color-warning)",
                      border: p.eligibility === "eligible" ? "1px solid var(--ds-color-success-soft)" : "1px solid var(--ds-color-warning-soft)",
                    }}
                  >
                    {p.eligibility === "eligible" ? "Eligible" : "Nearly eligible"}
                  </span>
                </div>
                <p className="text-[var(--ds-color-ink-muted)] mt-1">{p.note}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.top_improvements?.length > 0 && (
        <div
          data-testid="mortgage-improvements"
          className="rounded-[var(--ds-radius-card)] p-5 space-y-3"
          style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)" }}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-ink-muted)]">Top improvements</div>
          <ul className="space-y-2.5">
            {data.top_improvements.map((imp, i) => (
              <li key={`${imp.bucket}-${i}`} data-testid={`mortgage-improvement-${imp.bucket}`} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-[var(--ds-color-ink)]">{imp.label}</span>
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px]"
                    style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)", border: "1px solid var(--ds-color-accent-soft)" }}
                  >
                    +{imp.impact_pts} pts
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PPRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[var(--ds-color-ink-muted)]">{label}</span>
      <span className="text-[var(--ds-color-ink)]">{fmtDollar(value)}</span>
    </div>
  );
}

function FieldGroup({ title, children }) {
  return (
    <section
      className="rounded-[var(--ds-radius-card)] p-5 space-y-4"
      style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)" }}
    >
      <h2 className="text-[10px] uppercase tracking-[0.22em] text-[var(--ds-color-ink-muted)]">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function NumField({ label, value, onChange, prefix, placeholder, step = "1", testId }) {
  return (
    <label className="block text-xs">
      <span className="block mb-1.5 text-[var(--ds-color-ink-muted)]">{label}</span>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[var(--ds-color-ink-muted)]">{prefix}</span>
        )}
        <input
          type="number"
          data-testid={testId}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          step={step}
          className={`min-w-0 w-full rounded-md border border-[var(--ds-color-border)] bg-transparent px-3 py-2 text-sm text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-accent)] ${prefix ? "pl-6" : ""}`}
        />
      </div>
    </label>
  );
}

function SelectField({ label, value, options, onChange, testId }) {
  return (
    <label className="block text-xs">
      <span className="block mb-1.5 text-[var(--ds-color-ink-muted)]">{label}</span>
      <select
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-[var(--ds-color-border)] bg-[var(--ds-color-background)] px-3 py-2 text-sm text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-accent)]"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function TextField({ label, value, onChange, placeholder, maxLength, testId }) {
  return (
    <label className="block text-xs">
      <span className="block mb-1.5 text-[var(--ds-color-ink-muted)]">{label}</span>
      <input
        type="text"
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="min-w-0 w-full rounded-md border border-[var(--ds-color-border)] bg-transparent px-3 py-2 text-sm text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-accent)]"
      />
    </label>
  );
}
